// src/components/crm/CloseDetailsModal.js
// SEAM 9 VERIFIER: qclose-3e8a5c17-modal
//
// "Why did this close?" — asked right after an estimate is won, lost or
// deferred, and from the estimate page's strip. Records the decision factor,
// how our price landed, and a note, through recordQuoteCloseDetails. The close
// itself is ALREADY recorded by the server (the stage move wrote it); this only
// adds the operator's side.
//
// SKIPPABLE IN ONE CLICK (roadmap: "forced fields produce garbage data"):
//   Skip     — dismisses the question for EVERY staff member and browser
//              (recordQuoteCloseDetails { dismiss: true }, stored server-side).
//   ✕ / Not now — closes this dialog and records nothing, so the estimate page
//              keeps asking.
//
// Pinned to ONE close: closeActivityId is sent with the answer, and the server
// refuses it if the estimate was reopened or closed again meanwhile.
//
// INTERNAL ONLY: never mount in a portal view. The answers live in
// organizations/{orgId}/quoteCloses — never on the quote, which the portal reads.
import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { X, Flag } from 'lucide-react';
import { functions } from '../../firebase/config';

export const OUTCOME_WORD = { won: 'won', lost: 'lost', deferred: 'deferred' };

// The org's options, plus the stored value when it is no longer in the list
// (renamed or removed since) — an edit must not silently drop an old answer.
const withCurrent = (options, current) =>
    current && !options.includes(current) ? [...options, current] : options;

const CloseDetailsModal = ({
    isOpen,
    onClose,
    quote,
    outcome,
    closeActivityId = null,
    record = null,
    vocabularies,
    docWord = 'Estimate',
}) => {
    const [reason, setReason] = useState('');
    const [priceFeedback, setPriceFeedback] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(null);    // null | 'save' | 'skip'
    const [error, setError] = useState('');

    // Prefill from an existing answer each time the dialog opens (editing).
    useEffect(() => {
        if (!isOpen) return;
        setReason(record?.reason || '');
        setPriceFeedback(record?.priceFeedback || '');
        setNote(record?.note || '');
        setError('');
        setBusy(null);
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!isOpen || !quote?.id) return null;

    const reasons = withCurrent(vocabularies?.closeReasons || [], record?.reason);
    const feedback = withCurrent(vocabularies?.priceFeedbackOptions || [], record?.priceFeedback);
    const word = OUTCOME_WORD[outcome || record?.outcome] || 'closed';
    const pin = closeActivityId || record?.closeActivityId || null;

    const call = async (payload, kind) => {
        if (busy) return;                        // single-fire guard
        setBusy(kind);
        setError('');
        try {
            await httpsCallable(functions, 'recordQuoteCloseDetails')({
                quoteId: quote.id,
                closeActivityId: pin,
                ...payload,
            });
            onClose();
        } catch (err) {
            console.error('CloseDetailsModal: save failed', err);
            setError(err?.message || 'Could not save. Please try again.');
            setBusy(null);
        }
    };

    const handleSave = (e) => {
        e.preventDefault();
        if (!reason && !priceFeedback && !note.trim()) {
            setError('Choose a reason, some price feedback or add a note — or press Skip.');
            return;
        }
        call({ reason: reason || null, priceFeedback: priceFeedback || null, note: note.trim() || null }, 'save');
    };

    const handleSkip = () => call({ dismiss: true }, 'skip');
    const handleNotNow = () => { if (!busy) onClose(); };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                        <Flag size={18} className="text-[#5F4EC9]" />
                        Why was {quote.quoteNumber || `this ${docWord.toLowerCase()}`} {word}?
                    </h3>
                    <button type="button" onClick={handleNotNow} disabled={!!busy}
                        title="Not now — ask again later"
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSave} className="px-5 py-4 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Decision factor</label>
                        <select
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            disabled={!!busy}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white disabled:opacity-60"
                        >
                            <option value="">Choose…</option>
                            {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">How did our price land?</label>
                        <select
                            value={priceFeedback}
                            onChange={(e) => setPriceFeedback(e.target.value)}
                            disabled={!!busy}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white disabled:opacity-60"
                        >
                            <option value="">Choose…</option>
                            {feedback.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Note <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            disabled={!!busy}
                            rows={3}
                            maxLength={2000}
                            placeholder="Anything worth remembering — who they chose, what tipped it"
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg disabled:opacity-60"
                        />
                    </div>

                    {error && <p className="text-sm text-red-600">{error}</p>}

                    <div className="flex justify-end gap-2 pt-1">
                        <button type="button" onClick={handleSkip} disabled={!!busy}
                            title="Don't ask about this one again"
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50">
                            {busy === 'skip' ? 'Skipping…' : 'Skip'}
                        </button>
                        <button type="submit" disabled={!!busy}
                            className="px-4 py-2 text-sm font-medium text-white bg-[#5F4EC9] rounded-lg hover:bg-[#4f40a8] disabled:opacity-50">
                            {busy === 'save' ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                    <p className="text-xs text-gray-400 pt-1">
                        Internal: the customer never sees this. Skip stops the question for everyone;
                        ✕ just closes it for now.
                    </p>
                </form>
            </div>
        </div>
    );
};

export default CloseDetailsModal;
